import { FormInitialState } from "./formStateObject";
import
{
    LgdFormControl,
    LgdFormControlBaseConfig,
    LgdFormControlFileConfig,
    LgdFormControlNumberConfig,
    LgdFormControlRadioConfig,
    LgdFormControlSelectConfig,
    LgdFormControlTextAreaConfig,
    LgdFormControlTextConfig,
    LgdFormControlTimeConfig,
    LgdFormDateControl,
    LgdNumberFormatType,
    LgdTypeaheadFormControl
} from "./lgdFormControl";
import { LgdCustomAsyncValidator, LgdCustomValidator, LgdValidators } from "./lgdValidators";
import { AugmentedFile, evaluate, ValueOrGetter } from "./model";

/*  eslint-disable @typescript-eslint/method-signature-style */
export interface LgdControlOptions
{
    updateOn?: "change" | "blur" | "submit";
}

export interface LgdTextControlOptions extends LgdControlOptions
{
    /**
     * Value or function that returns the control's minimum length state.
     */
    minLength?: ValueOrGetter<number>;
    /**
     * Value or function that returns the control's maximum length state.
     */
    maxLength?: ValueOrGetter<number>;
}

export interface LgdTextAreaControlOptions extends LgdTextControlOptions
{
    /** Number of rows to display. */
    rows?: ValueOrGetter<number>;
}

export interface LgdNumberControlOptions extends LgdControlOptions
{
    /**
     * Value or function that returns the control's minimum state.
     */
    min?: ValueOrGetter<number>;
    /**
     * Value or function that returns the control's maximum state.
     */
    max?: ValueOrGetter<number>;
    /**
     * Value or function that returns the control's precision state.
     */
    precision?: ValueOrGetter<number>;
    /**
     * Value or function that returns the control's scale state.
     */
    scale?: ValueOrGetter<number>;
    /**
     * Determines how the value should be displayed to the user.
     * A Value of "decimal" will use the `formatNumber` from @angular/common, and "percent" will use `formatPercent` from @angular/common.
     * Defaults to "decimal".
     *
     * @see https://v17.angular.io/api/common/formatNumber
     * @see https://v17.angular.io/api/common/formatPercent
     */
    filter?: LgdNumberFormatType;
    /**
     * Value or function that returns the control's noCommas state.
     */
    noCommas?: ValueOrGetter<boolean>;
}

export interface LgdCurrencyControlOptions extends LgdControlOptions
{
    /**
     * Value or function that returns the control's minimum state.
     */
    min?: ValueOrGetter<number>;
    /**
     * Value or function that returns the control's maximum state.
     */
    max?: ValueOrGetter<number>;
    /**
     * Value or function that returns the control's precision state.
     */
    precision?: ValueOrGetter<number>;
    /**
     * Value or function that returns the control's scale state.
     */
    scale?: ValueOrGetter<number>;
}

export interface LgdDateControlOptions extends LgdControlOptions
{
    /**
     * Value or function that returns the control's minimum Date.
     */
    min?: ValueOrGetter<Date>;
    /**
     * Value or function that returns the control's maximum Date.
     */
    max?: ValueOrGetter<Date>;
}

/**
 * TODO - These config options are mostly just placeholders until we have an actual implementation to code against.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/time
 */
export interface LgdTimePickerConfigOptions extends LgdControlOptions
{
    /**
     * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/time#time_value_format
     */
    readonly min?: ValueOrGetter<string>;
    /**
     * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/time#time_value_format
     */
    readonly max?: ValueOrGetter<string>;
    /**
     * For time inputs, the value of step is given in seconds, with a scaling factor of 1000 (since the underlying
     * numeric value is in milliseconds). The default value of step is 60, indicating 60 seconds (or 1 minute, or
     * 60,000 milliseconds).
     */
    readonly step?: ValueOrGetter<number>;
}

export interface LgdFileControlOptions extends LgdControlOptions
{
    /**
     * Value or function that returns the control's maximum file size in Bytes.
     */
    maxSize?: ValueOrGetter<number>;
}

export interface LgdTypeAheadControlOptions<TOption = unknown, TValue = TOption> extends LgdControlOptions
{
    displayValueFn?: (x: TOption) => string;
    valueFn?: (x: TOption) => TValue;
    autoActiveFirstOption?: boolean;
    freeForm?: boolean;
}

export interface LgdSelectControlOptions<TOption = unknown, TValue = unknown> extends LgdControlOptions
{
    displayValueFn?: (x: TOption) => string;
    valueFn?: (x: TOption) => TValue;
}

export interface LgdRadioGroupControlOptions<T> extends LgdControlOptions
{
    displayValueFn?: (x: T) => string;
}
/*  eslint-enable @typescript-eslint/method-signature-style */

class LgdFormControlFactoryImpl
{
    /**
     * Creates a LgdFormControl that is pre-configured with the standard validators and configuration for select fields.
     *
     * @param formState initial state
     * @param required Value or function that returns the control's required state.
     *
     * @see https://angular.io/guide/reactive-forms
     * @see FormControl
     */
    select<TOption = unknown, TValue = unknown>(
        formState?: FormInitialState<TValue>,
        required?: ValueOrGetter<boolean>,
        options: LgdSelectControlOptions<TOption, TValue> = {},
        customValidators: ReadonlyArray<LgdCustomValidator> = [],
        customAsyncValidators: ReadonlyArray<LgdCustomAsyncValidator> = []
    ): LgdFormControl<LgdFormControlSelectConfig<TOption, TValue>, TValue>
    {
        const validators = [
            LgdValidators.getRequiredValidator(required),
            ...customValidators.map(x => x.getValidatorFn()),
        ];

        const asyncValidators = customAsyncValidators ? customAsyncValidators.map(x => x.getValidatorFn()) : [];

        return new LgdFormControl<LgdFormControlSelectConfig<TOption, TValue>, TValue>(
            formState,
            {
                updateOn: options.updateOn || "change",
                validators,
                asyncValidators,
            },
            {
                required: required,
                displayValueFn: options.displayValueFn,
                // comparatorFn: options.comparatorFn,
                valueFn: options.valueFn,
                customValidators: [

                    ...customValidators,
                    ...customAsyncValidators,
                ]
            }
        );
    }

    /**
     * Creates a LgdFormControl that is pre-configured with the standard validators and configuration for typeahead fields.
     *
     * @param formState initial state
     * @param required Value or function that returns the control's required state.
     *
     * @see https://angular.io/guide/reactive-forms
     * @see FormControl
     */
    typeahead<TOption = unknown, TValue = TOption>(
        formState?: FormInitialState<TOption>,
        required?: ValueOrGetter<boolean>,
        options: LgdTypeAheadControlOptions<TOption, TValue> = {},
        customValidators: ReadonlyArray<LgdCustomValidator> = [],
        customAsyncValidators: ReadonlyArray<LgdCustomAsyncValidator> = []
    ): LgdTypeaheadFormControl<TOption, TValue>
    {
        const validators = [
            LgdValidators.getRequiredValidator(required),
            ...customValidators.map(x => x.getValidatorFn()),
        ];

        const asyncValidators = customAsyncValidators ? customAsyncValidators.map(x => x.getValidatorFn()) : [];

        return new LgdTypeaheadFormControl<TOption, TValue>(
            formState,
            {
                updateOn: options.updateOn || "change",
                validators,
                asyncValidators,
            },
            {
                required: required,
                displayValueFn: options.displayValueFn,
                valueFn: options.valueFn,
                autoActiveFirstOption: options.autoActiveFirstOption,
                customValidators: [

                    ...customValidators,
                    ...customAsyncValidators,
                ]
            }
        );
    }

    /**
     * Creates a LgdFormControl that is pre-configured with the standard validators and configuration for text fields.
     *
     * @param formState initial state
     * @param required Value or function that returns the control's required state.
     *
     * @see https://angular.io/guide/reactive-forms
     * @see FormControl
     *
     * Standard validators are:
     * @see LgdValidators.getRequiredValidator
     * @see LgdValidators.getMinLengthValidator
     * @see LgdValidators.getMaxLengthValidator
     */
    text(
        formState?: FormInitialState<string>,
        required?: ValueOrGetter<boolean>,
        options: LgdTextControlOptions = {},
        customValidators: ReadonlyArray<LgdCustomValidator> = [],
        customAsyncValidators: ReadonlyArray<LgdCustomAsyncValidator> = []
    ): LgdFormControl<LgdFormControlTextConfig, string>
    {
        const validators = [
            LgdValidators.getRequiredValidator(required),
            LgdValidators.getMinLengthValidator(options.minLength),
            LgdValidators.getMaxLengthValidator(options.maxLength),
            ...customValidators.map(x => x.getValidatorFn()),
        ];

        const asyncValidators = customAsyncValidators ? customAsyncValidators.map(x => x.getValidatorFn()) : [];

        return new LgdFormControl<LgdFormControlTextConfig, string>(
            formState,
            {
                updateOn: options.updateOn || "change",
                validators,
                asyncValidators,
            },
            {
                required: required,
                minLength: options.minLength,
                // todo - should this be configurable?
                showMaxLengthHint: () => evaluate(options.maxLength) > 0,
                maxLength: options.maxLength,
                customValidators: [

                    ...customValidators,
                    ...customAsyncValidators,
                ]
            }
        );
    }

    /**
     * Creates a LgdFormControl that is pre-configured with the standard validators and configuration for text areas.
     *
     * @param formState initial state
     * @param required Value or function that returns the control's required state.
     *
     * @see https://angular.io/guide/reactive-forms
     * @see FormControl
     */
    textarea(
        formState?: FormInitialState<string>,
        required?: ValueOrGetter<boolean>,
        options: LgdTextAreaControlOptions = {},
        customValidators: ReadonlyArray<LgdCustomValidator> = [],
        customAsyncValidators: ReadonlyArray<LgdCustomAsyncValidator> = []
    ): LgdFormControl<LgdFormControlTextAreaConfig, string>
    {
        const validators = [
            LgdValidators.getRequiredValidator(required),
            LgdValidators.getMinLengthValidator(options.minLength),
            LgdValidators.getMaxLengthValidator(options.maxLength),
            ...customValidators.map(x => x.getValidatorFn()),
        ];

        const asyncValidators = customAsyncValidators ? customAsyncValidators.map(x => x.getValidatorFn()) : [];

        return new LgdFormControl<LgdFormControlTextAreaConfig, string>(
            formState,
            {
                updateOn: options.updateOn || "change",
                validators,
                asyncValidators,
            },
            {
                required: required,
                minLength: options.minLength,
                // todo - should this be configurable?
                showMaxLengthHint: () => evaluate(options.maxLength) > 0,
                maxLength: options.maxLength,
                rows: options.rows,
                customValidators: [

                    ...customValidators,
                    ...customAsyncValidators,
                ]
            }
        );
    }

    /**
     * Creates a LgdFormControl that is pre-configured with the standard validators and configuration for numeric fields.
     *
     * The "percent" filter option assumes that you are passing in a simple percent value, i.e. 100 >= x >= 0.
     *
     * @param formState initial state
     * @param required Value or function that returns the control's required state.
     *
     * @see https://angular.io/guide/reactive-forms
     * @see FormControl
     */
    number(
        formState?: FormInitialState<number | string>,
        required?: ValueOrGetter<boolean>,
        options: LgdNumberControlOptions = {},
        customValidators: ReadonlyArray<LgdCustomValidator> = [],
        customAsyncValidators: ReadonlyArray<LgdCustomAsyncValidator> = []
    ): LgdFormControl<LgdFormControlNumberConfig, number | string>
    {
        const validators = [
            LgdValidators.getNumberRequiredValidator(required),
            LgdValidators.getIsNumberValidator(),
            LgdValidators.getMinValidator(options.min),
            LgdValidators.getMaxValidator(options.max),
            LgdValidators.getPrecisionValidator(options.precision),
            LgdValidators.getScaleValidator(options.scale),
            ...customValidators.map(x => x.getValidatorFn()),
        ];

        const asyncValidators = customAsyncValidators ? customAsyncValidators.map(x => x.getValidatorFn()) : [];

        return new LgdFormControl<LgdFormControlNumberConfig, number | string>(
            formState,
            {
                updateOn: options.updateOn,
                validators,
                asyncValidators,
            },
            {
                required: required,
                min: options.min,
                max: options.max,
                precision: options.precision,
                scale: options.scale,
                filter: options.filter,
                noCommas: options.noCommas,
                customValidators: [

                    ...customValidators,
                    ...customAsyncValidators,
                ]
            }
        );
    }

    /**
     * Creates a LgdFormControl that is pre-configured with the standard validators and configuration for currency fields.
     *
     * @param formState initial state
     * @param required Value or function that returns the control's required state.
     *
     * @see https://angular.io/guide/reactive-forms
     * @see FormControl
     */
    currency(
        formState?: FormInitialState<number | string>,
        required?: ValueOrGetter<boolean>,
        options: LgdCurrencyControlOptions = {},
        customValidators: ReadonlyArray<LgdCustomValidator> = [],
        customAsyncValidators: ReadonlyArray<LgdCustomAsyncValidator> = []
    ): LgdFormControl<LgdFormControlNumberConfig, number | string>
    {
        const validators = [
            LgdValidators.getRequiredValidator(required),
            LgdValidators.getIsCurrencyValidator(),
            LgdValidators.getMaxValidator(options.max),
            LgdValidators.getMinValidator(options.min),
            LgdValidators.getPrecisionValidator(options.precision),
            LgdValidators.getScaleValidator(options.scale),
            ...customValidators.map(x => x.getValidatorFn()),
        ];

        const asyncValidators = customAsyncValidators ? customAsyncValidators.map(x => x.getValidatorFn()) : [];

        return new LgdFormControl<LgdFormControlNumberConfig, number | string>(
            formState,
            {
                updateOn: options.updateOn,
                validators,
                asyncValidators,
            },
            {
                required: required,
                min: options.min,
                max: options.max,
                precision: options.precision,
                scale: options.scale,
                customValidators: [

                    ...customValidators,
                    ...customAsyncValidators,
                ]
            }
        );
    }

    /**
     * Creates a LgdFormControl that is pre-configured with the standard validators and configuration for date fields.
     *
     * @param formState initial state
     * @param required Value or function that returns the control's required state.
     *
     * @see https://angular.io/guide/reactive-forms
     * @see FormControl
     */
    date(
        formState?: FormInitialState<Date | string>,
        required?: ValueOrGetter<boolean>,
        options: LgdDateControlOptions = {},
        customValidators: ReadonlyArray<LgdCustomValidator> = [],
        customAsyncValidators: ReadonlyArray<LgdCustomAsyncValidator> = []
    ): LgdFormDateControl
    {
        const validators = [
            LgdValidators.getRequiredValidator(required),
            ...customValidators.map(x => x.getValidatorFn()),
        ];

        const asyncValidators = customAsyncValidators ? customAsyncValidators.map(x => x.getValidatorFn()) : [];

        return new LgdFormDateControl(
            formState,
            {
                updateOn: options.updateOn,
                validators,
                asyncValidators,
            },
            {
                required: required,
                min: options.min,
                max: options.max,
                customValidators: [

                    ...customValidators,
                    ...customAsyncValidators,
                ]
            }
        );
    }

    /**
     * TODO - We don't have a actual time picker component to use, yet.
     * !!! WARNING - THIS IS A WORK IN PROGRESS. API Surface may change !!!
     *
     * Creates a LgdFormControl that is pre-configured with the standard validators and configuration for time fields.
     *
     * @param formState initial state
     * @param required Value or function that returns the control's required state.
     *
     * @see https://angular.io/guide/reactive-forms
     * @see FormControl
     */
    time(
        formState?: FormInitialState<string>,
        required?: ValueOrGetter<boolean>,
        options: LgdTimePickerConfigOptions = {},
        customValidators: ReadonlyArray<LgdCustomValidator> = [],
        customAsyncValidators: ReadonlyArray<LgdCustomAsyncValidator> = []
    ): LgdFormControl<LgdFormControlTimeConfig, string>
    {
        const validators = [
            LgdValidators.getRequiredValidator(required),
            LgdValidators.getMinTimeValidator(options.min),
            LgdValidators.getMaxTimeValidator(options.max),
            ...customValidators.map(x => x.getValidatorFn()),
        ];

        const asyncValidators = customAsyncValidators ? customAsyncValidators.map(x => x.getValidatorFn()) : [];

        return new LgdFormControl<LgdFormControlTimeConfig, string>(
            formState,
            {
                updateOn: options.updateOn,
                validators,
                asyncValidators,
            },
            {
                required: required,
                min: options.min,
                max: options.max,
                step: options.step,
                customValidators: [

                    ...customValidators,
                    ...customAsyncValidators,
                ]
            }
        );
    }

    /**
     * Creates a LgdFormControl that is pre-configured with the standard validators and configuration for checkbox fields.
     *
     * @param formState initial state
     * @param required Value or function that returns the control's required state.
     *
     * @see https://angular.io/guide/reactive-forms
     * @see FormControl
     */
    checkbox(
        formState?: FormInitialState<boolean>,
        required?: ValueOrGetter<boolean>, // Todo - required booleans don't make sense.
        options: LgdControlOptions = {}
    ): LgdFormControl<LgdFormControlBaseConfig, boolean>
    {
        const validators = [LgdValidators.getRequiredValidator(required)];

        return new LgdFormControl<LgdFormControlBaseConfig, boolean>(
            formState,
            {
                updateOn: options.updateOn,
                validators,
            },
            {
                required: required,
            }
        );
    }

    /**
     * Creates a LgdFormControl that is pre-configured with the standard validators and configuration for radio groups.
     *
     * @param formState initial state
     * @param required Value or function that returns the control's required state.
     *
     * @see https://angular.io/guide/reactive-forms
     * @see FormControl
     */
    radioGroup<T>(
        formState?: FormInitialState<T>,
        required?: ValueOrGetter<boolean>,
        options: LgdRadioGroupControlOptions<T> = {}
    ): LgdFormControl<LgdFormControlRadioConfig<T>, T>
    {
        const validators = [LgdValidators.getRequiredValidator(required)];

        return new LgdFormControl<LgdFormControlSelectConfig<T>, T>(
            formState,
            {
                updateOn: options.updateOn || "change",
                validators,
            },
            {
                required: required,
                displayValueFn: options.displayValueFn,
            }
        );
    }

    /**
     * Creates a LgdFormControl that is pre-configured with the standard validators and configuration for File fields.
     *
     * @param formState initial state
     * @param required Value or function that returns the control's required state.
     *
     * @see https://angular.io/guide/reactive-forms
     * @see FormControl
     */
    file(
        formState?: FormInitialState<AugmentedFile>,
        required?: ValueOrGetter<boolean>,
        options: LgdFileControlOptions = {}
    ): LgdFormControl<LgdFormControlFileConfig, AugmentedFile>
    {
        const validators = [
            LgdValidators.getRequiredValidator(required),
            LgdValidators.getMaxFileSizeValidator(options.maxSize),
        ];

        return new LgdFormControl<LgdFormControlFileConfig, AugmentedFile>(
            formState,
            {
                updateOn: options.updateOn,
                validators,
            },
            {
                required: required,
                maxSize: options.maxSize,
            }
        );
    }
}

export const LgdFormControlFactory = new LgdFormControlFactoryImpl();
